// OAuth Callback Page - Handles the Supabase OAuth redirect
// This page receives the authorization code from Supabase and stores it in localStorage
// for the main app to pick up and complete the authentication flow.

import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { StatusScreen } from '@/components/StatusScreen';

export default function OAuthCallback() {
    const location = useLocation();
    const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
    const [errorMessage, setErrorMessage] = useState<string>('');

    useEffect(() => {
        // Function to process the OAuth callback
        const processCallback = () => {
            // Get the full URL to debug
            const fullUrl = window.location.href;
            const search = window.location.search;
            const hash = window.location.hash;

            console.log('[OAuthCallback] Full URL:', fullUrl);
            console.log('[OAuthCallback] Search:', search);
            console.log('[OAuthCallback] Hash:', hash);

            // Try to get code from query string first (standard OAuth2)
            const searchParams = new URLSearchParams(search);
            let code = searchParams.get('code');
            let error = searchParams.get('error');
            let errorDescription = searchParams.get('error_description');

            // If not in query string, try hash (some providers use fragment)
            if (!code && !error && hash) {
                const hashContent = hash.startsWith('#') ? hash.substring(1) : hash;
                const hashParams = new URLSearchParams(hashContent);
                code = hashParams.get('code');
                error = hashParams.get('error');
                errorDescription = hashParams.get('error_description');
            }

            console.log('[OAuthCallback] Parsed values:', {
                code: code ? code.substring(0, 8) + '...' : null,
                error,
                errorDescription
            });

            if (error) {
                setStatus('error');
                setErrorMessage(error + (errorDescription ? ': ' + errorDescription : ''));
                localStorage.setItem('supabase-oauth-error', JSON.stringify({
                    error,
                    description: errorDescription || '',
                    timestamp: Date.now()
                }));
                return true;
            }

            if (!code) {
                setStatus('error');
                setErrorMessage("Aucun code d'autorisation reçu (paramètre 'code' manquant).");
                localStorage.setItem('supabase-oauth-error', JSON.stringify({
                    error: 'no_code',
                    timestamp: Date.now()
                }));
                return false;
            }

            // Success!
            setStatus('success');
            const data = { code, timestamp: Date.now() };

            // Store in localStorage
            localStorage.setItem('supabase-oauth-code', JSON.stringify(data));
            console.log('[OAuthCallback] Code stored in localStorage');

            // Also try postMessage and BroadcastChannel
            try {
                if (window.opener) {
                    window.opener.postMessage({ type: 'supabase-oauth-success', code }, '*');
                    console.log('[OAuthCallback] postMessage sent');
                }
            } catch (e) { /* ignore */ }

            try {
                const bc = new BroadcastChannel('supabase-oauth');
                bc.postMessage({ type: 'success', code });
                bc.close();
                console.log('[OAuthCallback] BroadcastChannel sent');
            } catch (e) { /* ignore */ }

            // Auto-close after delay
            setTimeout(() => window.close(), 1500);
            return true;
        };

        // Try immediately
        const success = processCallback();

        // If failed, try again after a short delay
        if (!success) {
            const timeoutId = setTimeout(() => {
                console.log('[OAuthCallback] Retrying after delay...');
                processCallback();
            }, 500); // Increased delay to 500ms
            return () => clearTimeout(timeoutId);
        }
    }, [location]); // Re-run if location changes

    if (status === 'loading') {
        return <StatusScreen status="loading" title="Connexion à Supabase…" />;
    }

    if (status === 'success') {
        return <StatusScreen status="success" title="Connexion réussie" meta="Cette fenêtre va se fermer." />;
    }

    return (
        <StatusScreen
            status="error"
            title="Erreur de connexion"
            meta={errorMessage}
            action={{ label: 'Fermer cette fenêtre', onClick: () => window.close() }}
        />
    );
}

